import type { HTMLAttributes, PropsWithChildren } from 'react'
import * as Dialog from '@radix-ui/react-dialog'

import { css, styled } from 'styled-components'

import { SpaceBetween, tk, zIndex } from '../../utils'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { LoadingSpinner } from '../LoadingSpinner'
import { Typography } from '../Typography'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'

export interface ModalProps {
  onClose: () => void
  open: boolean
}

interface StyledModalProps {
  $size: ModalSize
}

const SpinnerWrapper = styled.div`
  display: flex;
  min-height: 300px;
  justify-content: center;
  align-items: center;
`

const StyledHeader = styled(SpaceBetween)`
  box-shadow: ${tk('shadow_md_bottom')};
  background-color: ${tk('color_palette_grey_50')};
  box-shadow: ${tk('shadow_md_bottom')};
  color: ${tk('color_semantic_text')};
  flex-shrink: 0; // needed for Safari 11/12
`

const StyledHeaderTitle = styled(Typography)`
  color: ${tk('color_semantic_text')};
  flex: 1;
  margin-left: 48px; // needed to center the title
  text-align: center;
`

const StyledFooter = styled(SpaceBetween)`
  padding: ${tk('spacing_sm')} ${tk('spacing_md')};
  box-shadow: ${tk('shadow_md_top')};
  background-color: ${tk('color_palette_grey_50')};
  flex-shrink: 0; // needed for Safari 11/12

  > * {
    flex-grow: 1;
  }
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${zIndex.modalOrMobilePopover}!important;
`

function Wrapper({ onClose, open, children }: PropsWithChildren<ModalProps>) {
  const onOpenChangeHandler = (newOpen: boolean) => {
    if (!newOpen) {
      onClose()
    }
  }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChangeHandler}>
      <Dialog.Portal>
        <Backdrop />
        <Dialog.Content asChild>{children}</Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export const Root = styled(Wrapper)``

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export function Spinner() {
  return (
    <SpinnerWrapper>
      <LoadingSpinner />
    </SpinnerWrapper>
  )
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export const Content = styled.div<StyledModalProps>`
  background-color: ${tk('color_palette_grey_100')};
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${zIndex.modalOrMobilePopover} !important;

  ${({ $size }) =>
    $size !== 'fullscreen'
    && css`
      @media ${tk('mq_tablet')} {
        top: 50%;
        left: 50%;
        bottom: auto;
        right: auto;
        transform: translate(-50%, -50%);
        border-radius: ${tk('radius_large')};
        max-height: calc(100% - ${tk('spacing_4xl')});
        overflow: hidden;
        max-width: calc(100% - ${tk('spacing_lg')});
        width: ${() => {
          switch ($size) {
            case 'md':
              return '640px'
            case 'lg':
              return '864px'
            case 'xl':
              return '1088px'
            default:
              return '418px'
          }
        }};
      }
    `}
`

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }
`

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export function Header({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <StyledHeader
      direction="x"
      spacing=""
      padding={tk('spacing_2xs')}
      alignItems="center"
      justifyContent="center"
      className={className}
    >
      {children}
    </StyledHeader>
  )
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export function HeaderTitle({
  text,
  as = 'h4',
  className,
}: {
  text: string
  as?: React.ElementType
  className?: string
}) {
  return (
    <StyledHeaderTitle as={as} $variant="body-lg-bold" className={className}>
      {text}
    </StyledHeaderTitle>
  )
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export function HeaderCloseButton() {
  return (
    <Dialog.Close asChild>
      <Button $hierarchy="tertiary" $withoutColor>
        <Icon glyph="IconCrossMedium" />
      </Button>
    </Dialog.Close>
  )
}

interface FooterProps extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {}

function RawFooter({ children, className }: FooterProps) {
  return (
    <StyledFooter
      className={className}
      direction="x"
      spacing={tk('spacing_xs')}
      alignItems="center"
      justifyContent="center"
    >
      {children}
    </StyledFooter>
  )
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export const Footer = styled(RawFooter)``
